import { LoaderCircleIcon, SparklesIcon } from 'lucide-react'
import { type RefObject, useCallback, useEffect, useState } from 'react'
import { Quill } from 'react-quill-new'

import { useGenerateReport, useListAssessmentReports } from '@/api/assessment-reports.hook'
import type { AssessmentReport } from '@/gen/inventory/v1/assessment_report_service_pb'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { toast } from '@/components/lemmala-design-system/toast/use-toast'

import { REGENERATE_REPORT_LABELS } from './labels'

export type RegenerateReportButtonProps = {
  ongoingAssessmentId: string
  quillRef: RefObject<Quill | null>
  onRegenerated?: () => void
}

export const RegenerateReportButton = ({
  ongoingAssessmentId,
  quillRef,
  onRegenerated,
}: RegenerateReportButtonProps) => {
  const { mutateAsync: generateReport, isPending } = useGenerateReport(ongoingAssessmentId)
  const [isOpen, setIsOpen] = useState(false)
  // Present only while a re-generate is in flight; wraps the baseline because the row may
  // not have existed beforehand. Drives both the hook's polling and the busy state.
  const [pendingRegeneration, setPendingRegeneration] = useState<{
    baseline?: AssessmentReport
  }>()
  const { regeneratedReport } = useListAssessmentReports(ongoingAssessmentId, pendingRegeneration)

  const handleRegenerate = useCallback(async () => {
    setIsOpen(false)
    try {
      // The RPC only queues the work and hands back the pre-regenerate row, which becomes
      // the baseline the shared reports query polls against until newer content lands.
      const { report: baseline } = await generateReport({ ongoingAssessmentId })
      setPendingRegeneration({ baseline })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: REGENERATE_REPORT_LABELS.ERROR_TITLE,
        description:
          error instanceof Error ? error.message : REGENERATE_REPORT_LABELS.ERROR_DESCRIPTION,
      })
      onRegenerated?.()
    }
  }, [generateReport, ongoingAssessmentId, onRegenerated])

  useEffect(() => {
    if (!regeneratedReport) return
    // Quill is uncontrolled once mounted, so the regenerated content has to be pushed in.
    if (quillRef.current) {
      const newContent = new TextDecoder().decode(regeneratedReport.content)
      const delta = quillRef.current.clipboard.convert({ html: newContent })
      quillRef.current.setContents(delta)
    }
    // oxlint-disable-next-line react/set-state-in-effect
    setPendingRegeneration(undefined)
    onRegenerated?.()
  }, [regeneratedReport, quillRef, onRegenerated])

  const isBusy = isPending || !!pendingRegeneration

  return (
    <>
      <Button variant='outline' onClick={() => setIsOpen(true)} disabled={isBusy}>
        {isBusy ? (
          <LoaderCircleIcon className='mr-2 size-4 animate-spin' />
        ) : (
          <SparklesIcon className='mr-2 size-4' />
        )}
        {isBusy ? REGENERATE_REPORT_LABELS.BUTTON_REGENERATING : REGENERATE_REPORT_LABELS.BUTTON}
      </Button>
      <Modal
        open={isOpen}
        variant='destructive'
        size='small'
        title={REGENERATE_REPORT_LABELS.MODAL_TITLE}
        subtitle={REGENERATE_REPORT_LABELS.MODAL_SUBTITLE}
        primaryButtonText={REGENERATE_REPORT_LABELS.MODAL_PRIMARY}
        cancelButtonProps={{ variant: 'outline' }}
        onCancel={() => setIsOpen(false)}
        onOk={handleRegenerate}
        confirmLoading={isBusy}
      />
    </>
  )
}
